const ROOT_DOMAIN = "layer.ai";

export interface EnvConfig {
  /** Public PostHog project API key for the matching stack (used for the next-app rollout flag). */
  posthogKey: string;
  /** The stack's web root, which serves the new app. */
  webAppUrl: string;
}

type StackName = "dev" | "dev2" | "app";

const EnvConfigs: Record<StackName, EnvConfig> = {
  dev: {
    posthogKey: "phc_A67BeEAAt9CYEeNK3BZZCJRpxYNMo5YxN5M8Hb2DnLMr",
    webAppUrl: "https://dev.layer.ai",
  },
  dev2: {
    posthogKey: "phc_vFaU6kVGSM4duNDgmJYMYrqt6zuDSJXdpdDPknD9HNxb",
    webAppUrl: "https://dev2.layer.ai",
  },
  app: {
    posthogKey: "phc_pw9H5YMECXjh5CctgPMmRzH3nPJxNnVAUXDhCtwwS2Ab",
    webAppUrl: "https://layer.ai",
  },
};

/**
 * The stack a hostname belongs to. This app answers on several names per stack —
 * `app.<web root>` and `legacy.<stack>` — so the stack is the last label before
 * `layer.ai`, and the root stack is the one with no label at all.
 */
const stackFor = (hostname: string): StackName => {
  if (hostname !== ROOT_DOMAIN && !hostname.endsWith(`.${ROOT_DOMAIN}`)) {
    // localhost, previews and anything else that is not a stack: treat as dev.
    return "dev";
  }
  const labels = hostname
    .slice(0, hostname.length - ROOT_DOMAIN.length)
    .split(".")
    .filter(Boolean);
  const stack = labels.at(-1);
  // Unrecognized layer.ai hosts get the production config so prod traffic never
  // points at a dev stack.
  return stack === "dev" || stack === "dev2" ? stack : "app";
};

export const getEnv = (): EnvConfig =>
  EnvConfigs[stackFor(window.location.hostname)];

/**
 * Login URL for the new app on the stack's web root. `returnTo` is an app-relative
 * path ("/acme/workflows/123") the user lands on after the auth handshake — the
 * login route only honors relative paths, so full URLs are ignored.
 */
export const getWebAppLoginUrl = (returnTo?: string): string => {
  const url = new URL("/api/auth/login", getEnv().webAppUrl);
  if (returnTo) {
    url.searchParams.set("returnTo", returnTo);
  }
  return url.toString();
};
